import type {
  Product,
  ProductTier,
  ProductTierInventory,
  ProductTierQuantity,
} from "gql/generated";

export const PRODUCT_SELLING_MODE = {
  Single: "SINGLE",
  Choice: "CHOICE",
} as const;

export type ProductSellingModeValue =
  (typeof PRODUCT_SELLING_MODE)[keyof typeof PRODUCT_SELLING_MODE];

type ProductRecurringSettings = {
  enabled?: boolean | null;
  billingIntervalMonths?: number | null;
  renewalType?: string | null;
  fixedRenewalMonth?: number | null;
  fixedRenewalDay?: number | null;
  proRateEnabled?: boolean | null;
  earlyPurchaseWindowDays?: number | null;
  multiListing?: {
    mode?: string | null;
    maxListings?: number | null;
  } | null;
};

type ProductSellingConfig = {
  mode?: string | null;
  basePrice?: number | null;
  options?: Array<ProductTier | null> | null;
  inventory?: ProductTierInventory | null;
  quantity?: ProductTierQuantity | null;
  recurring?: ProductRecurringSettings | null;
};

type ProductWithSellingConfig = Product & {
  sellingConfig?: ProductSellingConfig | null;
};

export type ResolvedProductSellingConfig = {
  mode: ProductSellingModeValue;
  basePrice: number;
  options: ProductTier[];
  inventory?: ProductTierInventory | null;
  quantity?: ProductTierQuantity | null;
  recurring?: ProductRecurringSettings | null;
};

export const getResolvedProductSellingConfig = (
  product?: ProductWithSellingConfig | null,
): ResolvedProductSellingConfig => {
  const options = (
    product?.sellingConfig?.options ??
    product?.productTiers ??
    []
  ).filter((option): option is ProductTier => option != null);

  return {
    mode:
      (product?.sellingConfig?.mode as ProductSellingModeValue | null) ??
      (options.length > 0 ? PRODUCT_SELLING_MODE.Choice : PRODUCT_SELLING_MODE.Single),
    basePrice:
      product?.sellingConfig?.basePrice ??
      (options.length > 0 ? 0 : (product?.pricing?.tiers?.[0]?.price ?? 0)),
    options,
    inventory:
      product?.sellingConfig?.inventory ?? product?.inventorySettings ?? undefined,
    quantity:
      product?.sellingConfig?.quantity ?? product?.quantitySettings ?? undefined,
    recurring:
      product?.sellingConfig?.recurring ??
      (product?.renewableFeeMetadata
        ? {
            enabled: true,
            billingIntervalMonths:
              product.renewableFeeMetadata.billingIntervalMonths,
            renewalType: product.renewableFeeMetadata.renewalType,
            fixedRenewalMonth: product.renewableFeeMetadata.fixedRenewalMonth,
            fixedRenewalDay: product.renewableFeeMetadata.fixedRenewalDay,
            proRateEnabled: product.renewableFeeMetadata.proRateEnabled,
            earlyPurchaseWindowDays:
              product.renewableFeeMetadata.earlyPurchaseWindowDays,
          }
        : undefined),
  };
};

export const getResolvedProductSellingOptions = (
  product?: ProductWithSellingConfig | null,
) => getResolvedProductSellingConfig(product).options;

export const getResolvedProductBasePrice = (
  product?: ProductWithSellingConfig | null,
) => getResolvedProductSellingConfig(product).basePrice;

export const isSellingOptionSoldOut = (option?: {
  inventory?: {
    enabled?: boolean | null;
    currentCount?: number | null;
  } | null;
} | null): boolean =>
  !!option?.inventory?.enabled && (option.inventory.currentCount ?? 0) <= 0;

/** True when every price tier (or the base price, if there are no tiers) is $0. */
export const isProductFreeForAllTiers = (
  product?: ProductWithSellingConfig | null,
): boolean => {
  const { basePrice, options } = getResolvedProductSellingConfig(product);
  return options.length > 0
    ? options.every(option => (option.price ?? 0) === 0)
    : (basePrice ?? 0) === 0;
};

/**
 * Compute the next occurrence of a fixed month/day on or after `from`.
 * Uses day-level granularity so frontend and backend match exactly.
 */
function getNextFixedDate(from: Date, month: number, day: number): Date {
  const year = from.getFullYear();
  let candidate = new Date(year, month - 1, day);
  if (candidate.getMonth() !== month - 1) {
    candidate = new Date(year, month, 0);
  }
  if (candidate <= from) {
    candidate = new Date(year + 1, month - 1, day);
    if (candidate.getMonth() !== month - 1) {
      candidate = new Date(year + 1, month, 0);
    }
  }
  return candidate;
}

function differenceInDays(a: Date, b: Date): number {
  const msPerDay = 86400000;
  return Math.round((a.getTime() - b.getTime()) / msPerDay);
}

function addMonthsSimple(date: Date, months: number): Date {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
}

/**
 * Calculate the pro-rated charge amount for a fixed-date renewal.
 * Returns the original amount if pro-rating doesn't apply.
 * Uses day-level granularity to match the backend calculation exactly.
 */
export function calculateProRatedAmount(
  amount: number,
  recurring: ProductRecurringSettings | null | undefined,
): number {
  if (
    !recurring?.proRateEnabled ||
    recurring.renewalType !== "FIXED_DATE" ||
    !recurring.fixedRenewalMonth ||
    !recurring.fixedRenewalDay
  ) {
    return amount;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const nextFixed = getNextFixedDate(
    today,
    recurring.fixedRenewalMonth,
    recurring.fixedRenewalDay,
  );
  const previousFixed = addMonthsSimple(nextFixed, -12);
  const totalDays = differenceInDays(nextFixed, previousFixed);
  const remainingDays = differenceInDays(nextFixed, today);

  // Inside the early-purchase window new joiners buy the next full year.
  if (remainingDays <= (recurring.earlyPurchaseWindowDays ?? 0)) {
    return amount;
  }
  const fractionRemaining = remainingDays / totalDays;

  if (fractionRemaining < 0.5) {
    const proRated = Math.round(amount * fractionRemaining);
    return proRated > 0 ? proRated : amount;
  }

  return amount;
}

/**
 * When today falls in a fixed-date product's early-purchase window, the dates
 * a new joiner's membership would cover. Null outside the window.
 */
export function getEarlyPurchaseWindow(
  recurring: ProductRecurringSettings | null | undefined,
): { freeDaysUntil: Date; periodEnd: Date; bonusDays: number } | null {
  if (
    !recurring?.proRateEnabled ||
    !recurring.earlyPurchaseWindowDays ||
    recurring.renewalType !== "FIXED_DATE" ||
    !recurring.fixedRenewalMonth ||
    !recurring.fixedRenewalDay
  ) {
    return null;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const nextFixed = getNextFixedDate(
    today,
    recurring.fixedRenewalMonth,
    recurring.fixedRenewalDay,
  );
  const bonusDays = differenceInDays(nextFixed, today);
  if (bonusDays > recurring.earlyPurchaseWindowDays) return null;

  return {
    freeDaysUntil: nextFixed,
    periodEnd: getNextFixedDate(
      nextFixed,
      recurring.fixedRenewalMonth,
      recurring.fixedRenewalDay,
    ),
    bonusDays,
  };
}
